import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { motion } from 'framer-motion';
import { 
  ArrowLeft, TrendingUp, TrendingDown, Flame, Target,
  CheckCircle, AlertTriangle, Calendar, Clock, Award,
  ChevronRight, Bell, BellOff, Download
} from 'lucide-react';

export default function WeeklyReport({ onNavigate }) {
  const { api, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [report, setReport] = useState(null);
  const [notificationsEnabled, setNotificationsEnabled] = useState(true);

  const fetchReport = useCallback(async () => {
    try {
      const res = await api.get('/parent/weekly-report');
      setReport(res.data);
    } catch (err) {
      console.error('Failed to fetch report:', err);
    }
    setLoading(false);
  }, [api]);

  useEffect(() => { fetchReport(); }, [fetchReport]);

  const toggleNotifications = async () => {
    try {
      await api.post('/parent/toggle-notifications', { 
        enabled: !notificationsEnabled 
      });
      setNotificationsEnabled(!notificationsEnabled);
    } catch (err) {
      console.error('Failed to toggle notifications:', err);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0f172a] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-violet-500/30 border-t-violet-500 rounded-full animate-spin" />
      </div>
    );
  }

  const subjectIcons = { Math: '📐', Science: '🧬', English: '📖', 'Social Science': '🌍', Hindi: '🔤', EVS: '🌿' };

  return (
    <div className="min-h-screen bg-[#0f172a] pb-20">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-[#0f172a]/95 backdrop-blur-sm border-b border-slate-800/50 px-4 py-3">
        <div className="max-w-lg mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => onNavigate('parent-dashboard')} className="text-slate-400 hover:text-white">
              <ArrowLeft className="w-6 h-6" />
            </button>
            <div>
              <h1 className="text-lg font-bold text-white font-['Outfit']">Weekly Report</h1>
              <p className="text-xs text-slate-500">
                {report?.week_start} - {report?.week_end}
              </p>
            </div>
          </div>
          <button
            onClick={toggleNotifications}
            className={`p-2 rounded-lg ${notificationsEnabled ? 'bg-violet-600' : 'bg-slate-800'}`}
          >
            {notificationsEnabled ? (
              <Bell className="w-5 h-5 text-white" />
            ) : (
              <BellOff className="w-5 h-5 text-slate-400" />
            )}
          </button>
        </div>
      </div>

      <div className="max-w-lg mx-auto px-4 pt-6 space-y-6">
        {/* Child Info */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-center gap-4 bg-slate-900/50 border border-slate-800 rounded-xl p-4"
        >
          <div className="w-14 h-14 rounded-full bg-gradient-to-br from-violet-500 to-cyan-500 flex items-center justify-center text-2xl font-bold text-white">
            {report?.child_name?.charAt(0) || 'S'}
          </div>
          <div className="flex-1">
            <p className="text-white font-medium">{report?.child_name || 'Student'}</p>
            <p className="text-xs text-slate-400">{report?.child_class}</p>
          </div>
          <div className="text-right">
            <div className="flex items-center gap-1 text-orange-400">
              <Flame className="w-4 h-4" />
              <span className="font-bold">{report?.streak || 0}</span>
            </div>
            <p className="text-xs text-slate-500">day streak</p>
          </div>
        </motion.div>

        {/* Summary Stats */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="grid grid-cols-3 gap-3"
        >
          <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-4 text-center">
            <Clock className="w-6 h-6 text-cyan-400 mx-auto mb-2" />
            <p className="text-xl font-bold text-white">{report?.total_time || '0m'}</p>
            <p className="text-xs text-slate-500">Study Time</p>
          </div>
          <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-4 text-center">
            <Target className="w-6 h-6 text-violet-400 mx-auto mb-2" />
            <p className="text-xl font-bold text-white">{report?.sessions_completed || 0}</p>
            <p className="text-xs text-slate-500">Sessions</p>
          </div>
          <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-4 text-center">
            <Award className="w-6 h-6 text-amber-400 mx-auto mb-2" />
            <p className="text-xl font-bold text-white">{report?.xp_earned || 0}</p>
            <p className="text-xs text-slate-500">XP Earned</p>
          </div>
        </motion.div>

        {/* Subject Performance */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <h2 className="text-lg font-semibold text-white mb-4">Subject Performance</h2>
          
          <div className="space-y-3">
            {(report?.subjects || []).map((subject, i) => {
              const improved = subject.change > 0;
              const color = subject.score >= 80 ? 'emerald' : subject.score >= 60 ? 'amber' : 'red';
              
              return (
                <div
                  key={i}
                  className="bg-slate-900/50 border border-slate-800 rounded-xl p-4"
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <span className="text-2xl">{subjectIcons[subject.name] || '📚'}</span>
                      <span className="text-white font-medium">{subject.name}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`text-lg font-bold text-${color}-400`}>{subject.score}%</span>
                      <div className={`flex items-center gap-1 text-xs ${
                        improved ? 'text-emerald-400' : subject.change < 0 ? 'text-red-400' : 'text-slate-500'
                      }`}>
                        {improved ? <TrendingUp className="w-3 h-3" /> : subject.change < 0 ? <TrendingDown className="w-3 h-3" /> : null}
                        {subject.change !== 0 && `${improved ? '+' : ''}${subject.change}%`}
                      </div>
                    </div>
                  </div>
                  
                  {/* Progress Bar */}
                  <div className="h-2 bg-slate-800 rounded-full overflow-hidden">
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: `${subject.score}%` }}
                      transition={{ duration: 0.5, delay: i * 0.1 }}
                      className={`h-full bg-${color}-500 rounded-full`}
                    />
                  </div>
                  
                  <p className="text-xs text-slate-500 mt-2">
                    {subject.sessions_count} session{subject.sessions_count !== 1 ? 's' : ''} this week
                  </p>
                </div>
              );
            })}
          </div>
        </motion.div>

        {/* Strengths & Areas to Improve */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="grid grid-cols-2 gap-4"
        >
          {/* Strengths */}
          <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-4">
            <div className="flex items-center gap-2 mb-3">
              <CheckCircle className="w-5 h-5 text-emerald-400" />
              <span className="text-emerald-300 font-medium text-sm">Strengths</span>
            </div>
            <ul className="space-y-2">
              {(report?.strengths || ['No data yet']).map((item, i) => (
                <li key={i} className="text-sm text-slate-300 flex items-start gap-2">
                  <span className="text-emerald-400">•</span>
                  {item}
                </li>
              ))}
            </ul>
          </div>

          {/* Needs Practice */}
          <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4">
            <div className="flex items-center gap-2 mb-3">
              <AlertTriangle className="w-5 h-5 text-amber-400" />
              <span className="text-amber-300 font-medium text-sm">Needs Practice</span>
            </div>
            <ul className="space-y-2">
              {(report?.needs_practice || ['No data yet']).map((item, i) => (
                <li key={i} className="text-sm text-slate-300 flex items-start gap-2">
                  <span className="text-amber-400">•</span>
                  {item}
                </li>
              ))}
            </ul>
          </div>
        </motion.div>

        {/* Recommendation */}
        {report?.recommendation && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="bg-violet-500/10 border border-violet-500/30 rounded-xl p-4"
          >
            <h3 className="text-sm font-medium text-violet-300 mb-2">💡 Recommendation</h3>
            <p className="text-slate-300 text-sm">{report.recommendation}</p>
            <button
              onClick={() => onNavigate('daily-loop')}
              className="mt-3 bg-violet-600 hover:bg-violet-700 text-white px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2"
            >
              Start Practice <ChevronRight className="w-4 h-4" />
            </button>
          </motion.div>
        )}

        {/* Download Report */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
        >
          <button
            className="w-full bg-slate-800 hover:bg-slate-700 text-white rounded-xl py-3 font-medium flex items-center justify-center gap-2"
          >
            <Download className="w-4 h-4" /> Download PDF Report
          </button>
        </motion.div>

        {/* Notification Info */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6 }}
          className="text-center"
        >
          <p className="text-xs text-slate-500">
            {notificationsEnabled 
              ? '📬 You\'ll receive this report every Sunday at 10 AM' 
              : '🔕 Weekly report notifications are disabled'}
          </p>
        </motion.div>
      </div>
    </div>
  );
}
